import { IInteractive } from "../../../interfaces/IInteractive";
import { IShowable } from "../../../interfaces/IShowable";
import { ScreenManager } from "../../../managers/ScreenManager";
import { ShowableEvents } from "../../../mixins/Showable";
import { OptionsItemLayoutData, OptionsItemLiveData, OptionsItemLocData, OptionsItemThemeData, OptionsItemLayoutDefinitionData } from "../../../types/types";
import { ScreenOrientationType } from "../../../utils/GetOrientation";
import { RectSize } from "../../../utils/RectSize";
import { LayoutDataManager } from "../../managers/LayoutDataManager";
import { AppSettings } from "../../managers/AppSettings";
import { PopupWindow } from "../../popup/PopupWindow";
import { OptionsItemContentType } from "./OptionsItem";
import { OptionsLeafPopupContentBase } from "./OptionsLeafPopupContentBase";
import { OptionsLeafPopupImageItemsContent } from "./OptionsLeafPopupImageItemsContent";
import { OptionsLeafPopupTextContent } from "./OptionsLeafPopupTextContent";
import { OptionsLeafPopupTextItemsContent } from "./OptionsLeafPopupTextItemsContent";
import { OptionsDataManager } from "../../managers/OptionsDataManager";
import { AppAudioPlayer, SoundEffectKeys } from "../../../audio/AppAudioPlayer";


export enum OptionsLeafPopupContentType {
    TEXT,
    TEXT_ITEMS,
    IMAGE_ITEMS,
}

/**
 * 
 * Inheritance: 
    OptionsLeafPopup: PopupWindow: Container
    > contents: OptionsLeafPopupContentBase[]

    OptionsLeafPopupImageItemsContent: OptionsLeafPopupContentBase: ScrollPane: Container
    > itemsContainer: ScrollPaneContent

    ScrollPaneContent: Container
 */
export class OptionsLeafPopup
    extends PopupWindow
    implements IInteractive, IShowable {
    /**
     * Contents of different types; 
     * - each with bg and list of items of said type
     */
    public contents: OptionsLeafPopupContentBase[];
    /**
     * Rect we use to redraw overlay
     * pivot at (0,0) - which also is pivot for this content;
     */
    protected overlayRect: RectSize;
    protected curContentType: number;
    // All live data; JSON Node ref; Handle with care!
    protected itemLayoutData: OptionsItemLayoutData;
    protected itemThemeData: OptionsItemThemeData;
    protected itemLocData: OptionsItemLocData;
    protected itemLiveData: OptionsItemLiveData;
    /**
     * Rect area allowed for content @see OptionsLeafPopupContentBase and it's subclasses.
     * - changes based on orientation and screen size @see OnResize @see OnOrientationChanged
     */
    protected contentSize: RectSize;
    /**
     * container within which popup content can animate position freely, 
     * while the container itself can be repositioned freely (snapped to botom right of screen inside popup container)
     */
    public contentContainer: Phaser.GameObjects.Container;
    /**
     * Callback for content items, when item is selected 
     * NOTE: only some contents have items (for ex TextContent has no items)
     */
    protected onContentItemSelected: Function;
    private eventEmitter: Phaser.Events.EventEmitter;


    public GetEventEmitter(): Phaser.Events.EventEmitter {
        return this.eventEmitter;
    }

    public Init(
        // onShowStart?: Function, 
        // onHideComplete?: Function,
    ): void {
        // > initialize screenOverlayHitArea
        super.Init();

        // this.onShowStart = onShowStart;
        // this.onHideComplete = onHideComplete;

        this.eventEmitter = new Phaser.Events.EventEmitter();
        this.contentSize = new RectSize();
        this.contents = [];
    }

    public Build(): void {
        // > build screenOverlay
        super.Build();

        this.contentContainer = this.scene.make.container({});
        this.add(this.contentContainer);

        let content: OptionsLeafPopupContentBase;
        // text content
        content = new OptionsLeafPopupTextContent(this.scene);
        content.Init(true, this.OnBtnBackClick.bind(this));
        content.Build();
        content.Hide(false);
        this.contentContainer.add(content);
        this.contents[OptionsItemContentType.TEXT] = content;

        // text items content
        content = new OptionsLeafPopupTextItemsContent(this.scene);
        content.Init(true, this.OnBtnBackClick.bind(this));
        content.Build();
        content.Hide(false, null, true); // animate?, onComplete?, ignoreCallbacks?
        this.contentContainer.add(content);
        this.contents[OptionsItemContentType.ITEMS_TEXT] = content;

        // image items content
        content = new OptionsLeafPopupImageItemsContent(this.scene);
        content.Init(true, this.OnBtnBackClick.bind(this));
        content.Build();
        content.Hide(false);
        this.contentContainer.add(content);
        this.contents[OptionsItemContentType.ITEMS_THUMBS] = content;
    }

    protected OnBtnBackClick(): void {
        // ui click sound
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click4);

        this.OnOverlayClick();
    }

    protected OnOverlayClick(): void {
        this.Hide();
        this.contents[this.curContentType].Hide();
    }

    public SetCallback(onContentItemSelected: Function = undefined): void {
        this.onContentItemSelected = onContentItemSelected;
    }

    /**
     * Set data
     * Determine content type from data
     * set current content type to detected content type
     * > you can now call Show() to show this popup
     */
    public SetData(
        itemLayoutData: OptionsItemLayoutData = undefined,
        itemThemeData: OptionsItemThemeData = undefined,
        itemLocData: OptionsItemLocData = undefined,
        itemLiveData: OptionsItemLiveData = undefined,
        setHelpText: boolean = false
    ): void {
        // console.log("SetData");
        // console.log(this, this.contents, OptionsItemType[itemLayoutData.itemType], OptionsItemContentType[itemLayoutData.contentType]);

        this.itemLayoutData = itemLayoutData;
        this.itemThemeData = itemThemeData;
        this.itemLocData = itemLocData;
        this.itemLiveData = itemLiveData;

        const itemLayoutDef: OptionsItemLayoutDefinitionData = LayoutDataManager.GetItemDefinitionData(itemLayoutData.id);
        const targetContentType = setHelpText ? OptionsItemContentType.TEXT : itemLayoutDef.contentType;

        // avoid contentType.NONE
        if (targetContentType >= 0) {
            // set data to new content type
            this.contents[targetContentType].SetCallback(this.OnContentItemSelected.bind(this));
            this.contents[targetContentType].SetData(
                itemLayoutData,
                itemThemeData,
                itemLocData,
                itemLiveData,
                setHelpText
            );

            // if (this.curContentType != targetContentType) {
            // // hide old content type
            // EDIT: you hide content with popup's Hide()
            // if (this.curContentType != undefined) {
            //     this.contents[this.curContentType].Hide(false);
            // }
            this.curContentType = targetContentType;
            // }
        }
    }

    /**
     * pass to parent..
     * @see OptionsCategoryContent.OnLeafPopupContentItemSelected
     */
    public OnContentItemSelected(selectedItemIndex: number): void {
        this.onContentItemSelected?.(selectedItemIndex);
    }

    /**
     * - Screen overlay alpha
     *   - OnShowStart: contents[i].show() - x/y transition content in with it's background
     */
    public override Show(animated: boolean = true): void {
        // console.log("OptionsLeafPopup::Show; animated=", animated);

        // kill tweens
        this.scene.tweens.killTweensOf(this.imgScreenOverlay);

        // reset
        // this.alpha = 0;
        // this.scale = 0;
        this.imgScreenOverlay.alpha = 0;

        // if animate
        // NOTE: this should be faster or at worst equal than content fade in
        const animDuration = OptionsDataManager.GetTiming(200);
        if (animated) {
            this.scene.tweens.add({
                // targets: this,
                targets: this.imgScreenOverlay,
                alpha: 0.3,
                // scale: 1,
                duration: animDuration,
                ease: AppSettings.ease.genericShowHideEase,
                onStart: () => {
                    // local
                    this.OnShowStart();
                    // external
                    // this.onShowStart?.();
                }
            })
        }
        else {
            //
            // this.scale = 1;
            // this.alpha = 1;
            this.imgScreenOverlay.alpha = 1;
            this.OnShowStart();
            // this.onShowStart?.();
        }
        this.isShowing = true;
    }

    public override Hide(
        animated: boolean = true,
        onComplete?: Function,
        // ignoreExternCallbacks:boolean = false
    ): void {
        // console.log("OptionsLeafPopup::Hide; animated=", animated);

        // this.OnHideStart();

        // kill tweens
        this.scene.tweens.killTweensOf(this.imgScreenOverlay);

        // ref

        // if animate
        // NOTE: this should be faster or at worst equal than content fade in
        let animDuration = OptionsDataManager.GetTiming(200);
        // animDuration = 5000;
        if (animated) {
            this.scene.tweens.add({
                // targets: this,
                targets: this.imgScreenOverlay,
                alpha: 0,
                // scale: 0,
                duration: animDuration,
                ease: AppSettings.ease.genericShowHideEase,
                // onStart: ()=>{
                //     // class local
                //     this.OnHideStart();
                // },
                onStart: this.OnHideStart.bind(this),
                onComplete: () => {
                    // function local
                    onComplete?.();
                    // external
                    // if (!ignoreExternCallbacks) {
                    //     this.onHideComplete?.();
                    // }
                    this.OnHideComplete();
                },
            })
        }
        else {
            // this.alpha = 0;
            // this.scale = 0;
            this.imgScreenOverlay.alpha = 0;
            // function callback
            onComplete?.();
            // external
            // this.onHideComplete?.();
            this.OnHideComplete();
        }
        this.isShowing = false;
    }

    protected OnShowStart(): void {
        // console.log("OnShowStart")
        this.RegisterListeners();

        // UpdateView content items
        // > SetInnerScale
        // > SetSize
        this.OnResizeManual();

        // also show content
        this.contents[this.curContentType].Show();

        this.eventEmitter.emit(ShowableEvents.SHOW_START);
    }

    protected OnShowComplete(): void {
        // console.log("OnShowComplete")
        // this.RegisterListeners();
    }

    protected OnHideStart(): void {
        // console.log("OnHideStart")
        this.UnregisterListeners();

        // also hide content
        this.contents[this.curContentType].Hide();

        // if needed
        // this.eventEmitter.emit(ShowableEvents.HIDE_START);
    }

    protected OnHideComplete(): void {
        this.eventEmitter.emit(ShowableEvents.HIDE_COMPLETE);
    }

    protected RegisterListeners(): void {
        // console.log("LeafPopup: reg");
        this.imgScreenOverlay.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnOverlayClick, this);
    }

    protected UnregisterListeners(): void {
        // console.log("LeafPopup: unreg");
        this.imgScreenOverlay.off(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.OnOverlayClick);
    }

    /**
     * Manually forces single OrResize call
     * - unique entry point
     *   - needs to manually calc windowRect
     */
    protected OnResizeManual(): void {
        // process resize
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect);
    }

    private OnOrientationChangedManual(): void {
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        this.OnOrientationChanged(curOrientation);
    }

    public OnResize(windowRect: RectSize): void {
        // console.log("OnResize", windowRect);
        // console.trace(this.isShowing)

        // > redraw overlay around (0,0) pivot
        super.OnResize(windowRect);

        // need innerContentScale calculation also in SetData
        const innerContentScale = this.GetInnerContentScale();

        // position content
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        switch (curOrientation) {
            case ScreenOrientationType.LANDSCAPE:
                this.contentSize.Set(windowRect.width * 0.7, windowRect.height);
                this.contents[this.curContentType].SetInnerScale(innerContentScale);
                this.contents[this.curContentType].SetSize(this.contentSize.width, this.contentSize.height);
                break;

            case ScreenOrientationType.PORTRAIT:
                this.contentSize.Set(windowRect.width, windowRect.height * 0.7);
                this.contents[this.curContentType].SetInnerScale(innerContentScale);
                this.contents[this.curContentType].SetSize(this.contentSize.width, this.contentSize.height);
                break;
        }

        /**
         * Content container always snaps to bottom right of screen
         */
        this.contentContainer.setPosition(windowRect.width, windowRect.height);
    }

    private GetInnerContentScale(): number {
        const curOrientation: ScreenOrientationType = ScreenManager.GetOrientation();
        switch (curOrientation) {
            case ScreenOrientationType.LANDSCAPE:
                // return ScreenManager.GetAssetScale(0.8, 0.9);
                return ScreenManager.GetAssetScale(0.8, 1.0);

            case ScreenOrientationType.PORTRAIT:
                // return ScreenManager.GetAssetScale(1.1, 0.9);
                return ScreenManager.GetAssetScale(1.1, 1.0);
        }
    }

    public OnOrientationChanged(screenOrientationType: ScreenOrientationType): void {
        // console.log("OnOrientationChanged")

        if (this.isShowing) {
            switch (screenOrientationType) {
                case ScreenOrientationType.LANDSCAPE:

                    break;
                case ScreenOrientationType.PORTRAIT:

                    break;
            }
        }
    }
}